import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import logo from "../assets/krs-logo-transparent.png";
import LeadsTable from "./LeadsTable";
import ImageManager from "./ImageManager";
import ProjectManager from "./ProjectManager";
import TextManager from "./TextManager";
import { LogOut, Users, Image as ImageIcon, ExternalLink, FolderOpen, UploadCloud, Check, Type } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [images, setImages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [justPublished, setJustPublished] = useState(false);

  const pending = images.filter((i) => i.dirty).length + projects.filter((p) => p.dirty).length + texts.filter((t) => t.dirty).length;

  const loadImages = useCallback(async () => {
    const res = await api.get("/admin/images");
    setImages(res.data);
  }, []);

  const loadProjects = useCallback(async () => {
    const res = await api.get("/admin/projects");
    setProjects(res.data);
  }, []);

  const loadTexts = useCallback(async () => {
    const res = await api.get("/admin/texts");
    setTexts(res.data);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("krs_token")) { navigate("/admin/login"); return; }
    (async () => {
      try {
        const meRes = await api.get("/auth/me");
        setEmail(meRes.data.email);
        const [leadsRes] = await Promise.all([api.get("/admin/leads"), loadImages(), loadProjects(), loadTexts()]);
        setLeads(leadsRes.data);
      } catch (e) {
        localStorage.removeItem("krs_token");
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, loadImages, loadProjects, loadTexts]);

  const logout = () => {
    localStorage.removeItem("krs_token");
    navigate("/admin/login");
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post("/admin/publish");
      await Promise.all([loadImages(), loadProjects(), loadTexts()]);
      setJustPublished(true);
      setTimeout(() => setJustPublished(false), 2500);
    } catch (e) {
      alert("Publish failed. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center"><p className="font-serif text-2xl text-navy">Loading…</p></div>;
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Top bar */}
      <header className="bg-navy sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
          <div className="flex items-center gap-4">
            <img src={logo} alt="KRS" className="h-12 w-auto" />
            <span className="hidden md:inline font-sans text-[11px] tracking-[0.22em] uppercase text-gold border-l border-cream/20 pl-4">Admin CRM</span>
          </div>
          <div className="flex items-center gap-5">
            {(pending > 0 || justPublished) && (
              <button onClick={publish} disabled={publishing || pending === 0} className={`inline-flex items-center gap-2 px-5 py-2.5 font-sans text-[11px] tracking-wide-nav uppercase transition-colors ${justPublished ? "bg-green-600 text-white" : "bg-gold text-navy hover:bg-gold-light"}`}>
                {justPublished ? <><Check size={15} /> Published</> : <><UploadCloud size={15} /> {publishing ? "Publishing…" : `Publish Changes (${pending})`}</>}
              </button>
            )}
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-2 font-sans text-[11px] tracking-wide-nav uppercase text-cream/70 hover:text-gold-soft transition-colors">View Site <ExternalLink size={13} /></a>
            <span className="hidden md:inline font-sans text-xs text-cream/50">{email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 font-sans text-[11px] tracking-wide-nav uppercase text-cream hover:text-gold transition-colors"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10">
        <div className="flex gap-2 border-b border-cream-dark mb-10 flex-wrap">
          <button onClick={() => setTab("leads")} className={`inline-flex items-center gap-2 px-6 py-4 font-sans text-[12px] tracking-wide-nav uppercase border-b-2 -mb-px transition-colors ${tab === "leads" ? "border-gold text-navy" : "border-transparent text-charcoal/50 hover:text-navy"}`}><Users size={16} /> Leads <span className="text-gold">({leads.length})</span></button>
          <button onClick={() => setTab("images")} className={`inline-flex items-center gap-2 px-6 py-4 font-sans text-[12px] tracking-wide-nav uppercase border-b-2 -mb-px transition-colors ${tab === "images" ? "border-gold text-navy" : "border-transparent text-charcoal/50 hover:text-navy"}`}><ImageIcon size={16} /> Images</button>
          <button onClick={() => setTab("projects")} className={`inline-flex items-center gap-2 px-6 py-4 font-sans text-[12px] tracking-wide-nav uppercase border-b-2 -mb-px transition-colors ${tab === "projects" ? "border-gold text-navy" : "border-transparent text-charcoal/50 hover:text-navy"}`}><FolderOpen size={16} /> Project Details</button>
          <button onClick={() => setTab("text")} className={`inline-flex items-center gap-2 px-6 py-4 font-sans text-[12px] tracking-wide-nav uppercase border-b-2 -mb-px transition-colors ${tab === "text" ? "border-gold text-navy" : "border-transparent text-charcoal/50 hover:text-navy"}`}><Type size={16} /> Home Content</button>
        </div>

        <div className="pb-24">
          {tab === "leads" && (
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-navy mb-2">Project Inquiries</h2>
              <p className="font-sans text-charcoal/60 mb-8">Everyone who has submitted a project through your website. Click a row for full details.</p>
              <LeadsTable leads={leads} />
            </div>
          )}
          {tab === "images" && (
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-navy mb-2">Site Images</h2>
              <p className="font-sans text-charcoal/60 mb-8">Hover any photo and click <strong>Replace</strong> to upload your own (before & after photos are each replaceable). Staged changes show a <strong>Pending</strong> badge — click <strong>Publish Changes</strong> at the top to push them live.</p>
              <ImageManager images={images} onChange={loadImages} />
            </div>
          )}
          {tab === "projects" && (
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-navy mb-2">Project Details</h2>
              <p className="font-sans text-charcoal/60 mb-8">Edit the title, location, type, and story for each portfolio project. Click <strong>Save Changes</strong> to stage, then <strong>Publish Changes</strong> at the top to push everything live.</p>
              <ProjectManager projects={projects} onChange={loadProjects} />
            </div>
          )}
          {tab === "text" && (
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-navy mb-2">Home Content</h2>
              <p className="font-sans text-charcoal/60 mb-8">Edit the titles &amp; locations shown under the three Before + After examples on the home page. Click <strong>Save</strong> to stage, then <strong>Publish Changes</strong> at the top to push live.</p>
              <TextManager texts={texts} onChange={loadTexts} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
